import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { ShieldCheck, UserPlus, Loader2, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CoachAdmins() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null);

  const load = () => api.get("/admin/list").then((r) => setAdmins(r.data.admins)).catch(() => {});
  useEffect(() => { load(); }, []);

  const errMsg = (err, fb) => {
    const d = err?.response?.data?.detail;
    toast.error(typeof d === "string" ? d : fb);
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/create", form);
      setForm({ name: "", email: "", password: "", role: "admin" });
      toast.success(form.role === "coach" ? "Nouveau coach créé" : "Nouvel administrateur créé");
      load();
    } catch (err) {
      errMsg(err, "Erreur");
    } finally { setBusy(false); }
  };

  const toggleStatus = async (a) => {
    setActing(a.user_id);
    try {
      await api.patch(`/admin/members/${a.user_id}/status`, { disabled: !a.disabled });
      toast.success(a.disabled ? "Compte réactivé" : "Compte désactivé");
      load();
    } catch (err) {
      errMsg(err, "Action impossible");
    } finally { setActing(null); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${a.name || a.email} ? Cette action est irréversible.`)) return;
    setActing(a.user_id);
    try {
      await api.delete(`/admin/members/${a.user_id}`);
      toast.success("Compte supprimé");
      load();
    } catch (err) {
      errMsg(err, "Suppression impossible");
    } finally { setActing(null); }
  };

  return (
    <CoachLayout>
      <div className="fade-up">
        <h1 className="text-2xl font-medium flex items-center gap-2"><ShieldCheck size={20} strokeWidth={1.6} /> Équipe (admins & coachs)</h1>
        <p className="mt-2 text-sm text-obsidian/50">Un <strong>admin</strong> accède à tout. Un <strong>coach</strong> accède uniquement à l'Agenda et aux Clients (pas aux tarifs, promos, parrainage ni à cette page). Crée ici d'autres membres.</p>

        <form onSubmit={create} className="mt-6 bg-white border border-border p-6 max-w-3xl grid sm:grid-cols-4 gap-3">
          <input data-testid="admin-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom"
            className="rounded-sm border border-border px-3 py-2.5 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
          <input data-testid="admin-new-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email"
            className="rounded-sm border border-border px-3 py-2.5 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
          <input data-testid="admin-new-password" type="text" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe (min. 6)"
            className="rounded-sm border border-border px-3 py-2.5 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
          <select data-testid="admin-new-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="rounded-sm border border-border px-3 py-2.5 text-sm focus:ring-1 focus:ring-sage focus:outline-none bg-white">
            <option value="admin">Admin (accès total)</option>
            <option value="coach">Coach (agenda + clients)</option>
          </select>
          <button data-testid="admin-new-submit" type="submit" disabled={busy}
            className="sm:col-span-4 justify-self-start rounded-sm bg-obsidian text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Créer le compte
          </button>
        </form>

        <div className="mt-8 border border-border max-w-3xl">
          {admins.map((a) => (
            <div key={a.user_id} data-testid={`admin-row-${a.user_id}`} className={`flex items-center justify-between bg-white border-b border-border last:border-b-0 px-4 py-3 ${a.disabled ? "opacity-60" : ""}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {a.name} {a.is_me && <span className="text-xs text-sage">(moi)</span>}
                  {a.disabled && <span data-testid={`admin-disabled-${a.user_id}`} className="text-[10px] tracking-wide uppercase bg-destructive/10 text-destructive px-1.5 py-0.5 rounded">désactivé</span>}
                </div>
                <div className="text-xs text-obsidian/50 truncate">{a.email}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className={`text-[11px] tracking-[0.14em] uppercase mr-2 ${a.role === "coach" ? "text-blue" : "text-obsidian/40"}`}>{a.role || "admin"}</span>
                {!a.is_me && (
                  <>
                    <button data-testid={`admin-toggle-${a.user_id}`} onClick={() => toggleStatus(a)} disabled={acting === a.user_id}
                      title={a.disabled ? "Réactiver" : "Désactiver"}
                      className={`p-2 rounded-sm hover:bg-secondary transition-[background-color] disabled:opacity-40 ${a.disabled ? "text-sage" : "text-obsidian/60"}`}>
                      {acting === a.user_id ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />}
                    </button>
                    <button data-testid={`admin-delete-${a.user_id}`} onClick={() => remove(a)} disabled={acting === a.user_id}
                      title="Supprimer" className="p-2 rounded-sm text-destructive hover:bg-destructive/10 transition-[background-color] disabled:opacity-40">
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </CoachLayout>
  );
}
